import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import ModalHeader from "../../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../../components/Modal/ModalFooter";
import ModalMain from "../../../../../components/Modal/ModalMain";
import { getSearchedAuditLogList } from "../../../../../service/AuditLogService";
import userContext from "../../../../../context/users/UserContext";
import loaderContext from "../../../../../context/loader/LoaderContext";
import themeContext from "../../../../../context/theme/ThemeContext";
import { CUSTOM_STYLES } from "../../../../../constants/Constants";
import { getConsistentDateFormat1 } from "../../../../IssuesAndTroubleshooting/helper/helper";
import "../../../StudyList.css";
import "../../../../../components/StyleListTable/StudyListTable.css";
import "./StudyAuditLogsModal.css";

const EllipsisTooltipCell = ({ value }) => {
  const text = value == null || value === "" ? "-" : String(value);
  return (
    <TooltipHost content={text}>
      <span className="table-cell-ellipsis-inner">{text}</span>
    </TooltipHost>
  );
};

const StudyAuditLogsModal = (props) => {
  const { show, onClose, studyId } = props;
  const { t } = useTranslation();
  const { getConsistentDateFormat, userTimeZone } = useContext(userContext);
  const { setLoaderRunning, ref } = useContext(loaderContext);
  const { themeName } = useContext(themeContext) || {};
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // OLD: const effectiveUserTimeZone = userTimeZone || localStorage.getItem("userTimeZone");
  const effectiveUserTimeZone = userTimeZone || sessionStorage.getItem("userTimeZone");

  const auditLogsScrollClass =
    themeName === "light"
      ? "common-modal-scroll-light"
      : "common-modal-scroll";

  useEffect(() => {
    if (!show || !studyId) return;
    let mounted = true;
    setError(null);
    setLoading(true);
    setLoaderRunning(true);
    ref?.current?.continuousStart?.();

    const body = {
      auditLogsSearch: {
        auditAction: null,
        auditLogsFrom: null,
        auditLogsTo: null,
        username: null,
        fromStudyListModal: true,
        entityId: String(studyId),
        auditLogsPage: 0,
        pageSize: 200,
      },
    };

    getSearchedAuditLogList(body)
      .then((response) => {
        if (!mounted) return;
        setLoaderRunning(false);
        ref?.current?.complete?.();
        if (response?.status === 200 && response?.data?.result === "SUCCESS") {
          const results = response?.data?.data?.results?.searchResults ?? [];
          setAuditLogs(Array.isArray(results) ? results : []);
        } else {
          setAuditLogs([]);
          setError(t("string-literals.error-msg") || "Failed to load audit logs.");
        }
      })
      .catch(() => {
        if (!mounted) return;
        setLoaderRunning(false);
        ref?.current?.complete?.();
        setAuditLogs([]);
        setError(t("string-literals.error-msg") || "Failed to load audit logs.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [show, studyId, t, setLoaderRunning, ref]);

  const getFormattedAuditTimestamp = (auditTimestamp) => {
    if (!auditTimestamp) return "-";
    return getConsistentDateFormat1(auditTimestamp, effectiveUserTimeZone);
  };


  const modalDetails = {
    width: "90%",
    maxHeight: "80vh",
    header: (
      <ModalHeader
        updatedModal={true}
        heading={
          t("study-list.actions.view-audit-logs")
        }
        handleCancel={onClose}
      />
    ),
    body: (
      <div className="study-audit-logs-modal-body">
        {loading ? (
          <div className="study-audit-logs-modal-loading">
            <Spinner size={SpinnerSize.medium} label={t("string-literals.loading") || "Loading..."} />
          </div>
        ) : error ? (
          <div className="study-audit-logs-modal-error">{error}</div>
        ) : auditLogs.length === 0 ? (
          <div className="study-audit-logs-modal-empty">
            {t("audit-logs.no-records") || "No audit logs found for this study."}
          </div>
        ) : (
          <div
            className={`${auditLogsScrollClass} study-audit-logs-modal-table-wrap`}
            style={{
              borderRadius: "10px",
              boxSizing: "border-box",
              maxHeight: "60vh",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <table
              className="study-audit-logs-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead className="study-list-thead1">
                <tr>
                  <th style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                    {t("audit-logs.entity-id")}
                  </th>
                  <th style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                    {t("audit-logs.audit-time-stamp")}
                  </th>
                  <th style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                    {t("audit-logs.action")}
                  </th>
                  <th style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                    {t("audit-logs.username")}
                  </th>
                  <th style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                    {t("audit-logs.new-value")}
                  </th>
                  <th style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                    {t("audit-logs.old-value")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((row, idx) => (
                  <tr key={row.id != null ? row.id : idx}>
                    <td style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                      {row.entityId ?? "-"}
                    </td>
                    <td style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                      {getFormattedAuditTimestamp(row.auditTimestamp)}
                    </td>
                    <td
                      style={{ border: "1px solid var(--border-primary)", padding: "8px" }}
                      className="study-audit-logs-cell-action"
                    >
                      <EllipsisTooltipCell value={row.action} />
                    </td>
                    <td style={{ border: "1px solid var(--border-primary)", padding: "8px" }}>
                      {row.username!='' ? row.username : "-"}
                    </td>
                    <td style={{ border: "1px solid var(--border-primary)", padding: "8px" }} className="study-audit-logs-cell-value">
                      {row.newValue!='' ? row.newValue : "-"}
                    </td>
                    <td style={{ border: "1px solid var(--border-primary)", padding: "8px" }} className="study-audit-logs-cell-value">
                      {row.oldValue!='' ? row.oldValue : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={[
          {
            title: t("button.close"),
            onclick: onClose,
          },
        ]}
      />
    ),
  };

  if (!show) return null;

  return (
    <ModalMain showModal={show} modalDetails={modalDetails} />
  );
};

export default StudyAuditLogsModal;
